import React, { useState, useEffect, useCallback } from 'react';
import { templatesApi, codesApi } from '../services/api';
import ICD10Search from '../components/ICD10Search';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  BookmarkSimple,
  Trash,
  Copy,
  Hash,
} from '@phosphor-icons/react';

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_code: '',
    modifier_1: '',
    modifier_2: '',
    diagnosis_code: '',
    place_of_service: '12',
    default_duration_minutes: 60,
  });

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, codesRes] = await Promise.all([
        templatesApi.getAll(),
        codesApi.getAll(),
      ]);
      setTemplates(templatesRes.data);
      setCodes(codesRes.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = () => {
    setFormData({
      name: '',
      description: '',
      service_code: '',
      modifier_1: '',
      modifier_2: '',
      diagnosis_code: '',
      place_of_service: '12',
      default_duration_minutes: 60,
    });
    setIsModalOpen(true);
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await templatesApi.create(formData);
      toast.success('Template created successfully');
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await templatesApi.delete(id);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const selectedCodeModifiers = codes.find((c) => c.code === formData.service_code)?.modifiers || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Claim Templates
          </h1>
          <p className="text-slate-500 mt-1">Create reusable templates for common claim types</p>
        </div>
        <Button
          onClick={handleOpenModal}
          className="bg-indigo-900 hover:bg-indigo-800"
          data-testid="create-template-btn"
        >
          <Plus size={18} className="mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <BookmarkSimple size={48} className="mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No templates yet</h3>
            <p className="text-slate-500 mb-4">
              Create templates for commonly used service types to speed up claim entry
            </p>
            <Button onClick={handleOpenModal} className="bg-indigo-900 hover:bg-indigo-800">
              <Plus size={18} className="mr-2" />
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => {
            const codeData = codes.find((c) => c.code === template.service_code);
            return (
              <Card key={template.id} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                        {template.name}
                      </CardTitle>
                      {template.description && (
                        <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(template.id)}
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Service Code</span>
                      <span className="font-mono font-semibold text-indigo-700">
                        {template.service_code}
                        {template.modifier_1 && `-${template.modifier_1}`}
                        {template.modifier_2 && `-${template.modifier_2}`}
                      </span>
                    </div>
                    {codeData && (
                      <p className="text-xs text-slate-500">{codeData.description}</p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Default Duration</span>
                      <span className="font-medium">{template.default_duration_minutes} min</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Times Used</span>
                      <span className="flex items-center gap-1 font-medium">
                        <Hash size={14} className="text-slate-400" />
                        {template.usage_count}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Template Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create Claim Template</DialogTitle>
            <DialogDescription>
              Define a reusable template for quick claim entry
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Personal Care Weekly"
                required
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Brief description of this template"
                data-testid="template-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_code">Service Code *</Label>
              <Select
                value={formData.service_code}
                onValueChange={(val) => handleChange('service_code', val)}
              >
                <SelectTrigger data-testid="template-code-select">
                  <SelectValue placeholder="Select service code" />
                </SelectTrigger>
                <SelectContent>
                  {codes.map((code) => (
                    <SelectItem key={code.code} value={code.code}>
                      <span className="font-mono">{code.code}</span> - {code.description.substring(0, 30)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCodeModifiers.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modifier 1</Label>
                  <Select
                    value={formData.modifier_1 || "none"}
                    onValueChange={(val) => handleChange('modifier_1', val === "none" ? "" : val)}
                  >
                    <SelectTrigger data-testid="template-modifier1-select">
                      <SelectValue placeholder="Select modifier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {selectedCodeModifiers.map((mod) => (
                        <SelectItem key={mod} value={mod}>
                          {mod}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Modifier 2</Label>
                  <Select
                    value={formData.modifier_2 || "none"}
                    onValueChange={(val) => handleChange('modifier_2', val === "none" ? "" : val)}
                  >
                    <SelectTrigger data-testid="template-modifier2-select">
                      <SelectValue placeholder="Select modifier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {selectedCodeModifiers.map((mod) => (
                        <SelectItem key={mod} value={mod}>
                          {mod}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Duration (min)</Label>
                <Input
                  type="number"
                  value={formData.default_duration_minutes}
                  onChange={(e) => handleChange('default_duration_minutes', parseInt(e.target.value))}
                  min={15}
                  step={15}
                  data-testid="template-duration-input"
                />
              </div>
              <div className="space-y-2">
                <ICD10Search
                  value={formData.diagnosis_code}
                  onChange={(code, desc) => handleChange('diagnosis_code', code)}
                  label="Diagnosis Code"
                  testId="template-diagnosis-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-900 hover:bg-indigo-800"
                disabled={saving}
                data-testid="save-template-btn"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Create Template'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
